import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { IoArrowBack } from "react-icons/io5";
import { SUBDOMAIN } from "../../../assets/meta/meta";
import QueryString from "qs";
import { getTicektListByPage } from "../../../api/service";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import useAutoLoadMore from "../../../hooks/useAutoLoadMore";
import PurchaseTicketModal from "../../../Components/modal/PurchaseTicketModal";
import TicketPurchaseDetailModal from "../../../Components/modal/TicketPurchaseDetailModal";
import TicketGiftModal from "../../../Components/modal/TicketGiftModal";
import {
  getPurchaseGiftBadgeLabel,
  getTicketPurchaseActions,
} from "./actionVisibility.mjs";
import {
  Card,
  CardHeader,
  StatusBadge,
  StatusDot,
  OrderInfo,
  OrderId,
  OrderDate,
  ProductName,
  ActionButtons,
  DetailButton,
  ContentArea,
} from "../../../Components/Common";
import { LoadingSpinner, EmptyMessage, FilterBadges } from "../../../Components/Common";
import { getStatusText } from "../../../utils/statusUtils";
import { getRemainingText, formatOrderId } from "../../../utils/formatters";

const FILTER_OPTIONS = ["전체", "사용가능", "완료"];

export default function TicketPurchaseList() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("전체");
  const [ticketModal, setTicketModal] = useState(false);
  const [ticketDetailModal, setTicketDetailModal] = useState(false);
  const [giftModal, setGiftModal] = useState(false);
  const [selectOrder, setSelectOrder] = useState<any>({});

  const query = useInfiniteQuery({
    queryKey: ["ticket-list"],
    queryFn: async ({ pageParam = 1 }) => {
      const qs = QueryString.stringify({ filter_site: SUBDOMAIN, filter_state: "" });
      const res = await getTicektListByPage({ page: pageParam, queryString: qs ? `&${qs}` : "" });
      return res.data;
    },
    getNextPageParam: (lastPage: any) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
  });

  const loadMoreRef = useAutoLoadMore({
    enabled: true,
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    onLoadMore: query.fetchNextPage,
    rootMargin: "160px 0px",
  });

  const allResults: any[] = (query.data?.pages.flatMap((p: any) => p.results) ?? []).filter(
    (ticket: any) => {
      if (filter === "사용가능") return ticket.state === "사용안함";
      if (filter === "완료") return ["사용함", "만료됨"].includes(ticket.state);
      return true;
    },
  );

  return (
    <React.Fragment>
      <MobileViewContainer>
        <div className="min-h-screen bg-gray-50 pb-24">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_0_#eee]">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
            >
              <IoArrowBack size={20} />
            </button>
            <h1 className="text-base font-semibold" style={{ fontFamily: "Gmarket-M" }}>
              티켓 내역
            </h1>
          </div>

          <FilterBadges
            options={FILTER_OPTIONS}
            activeFilter={filter}
            onFilterChange={setFilter}
          />

          <ContentArea>
            {query.isLoading ? (
              <LoadingSpinner height="200px" />
            ) : allResults.length === 0 ? (
              <EmptyMessage message="티켓 내역이 없습니다." />
            ) : (
              <>
                {allResults.map((ticket: any) => {
                  const actions = getTicketPurchaseActions(ticket);
                  const giftBadgeLabel = getPurchaseGiftBadgeLabel(ticket);

                  return (
                    <Card key={ticket.id}>
                      <CardHeader>
                        <StatusBadge status={ticket.state}>
                          <StatusDot status={ticket.state} />
                          {getStatusText(ticket.state)}
                        </StatusBadge>
                        <OrderInfo>
                          <OrderId>{formatOrderId(ticket.id, 8, "T-")}</OrderId>
                          <OrderDate>{getRemainingText(ticket.expiration_date)}</OrderDate>
                        </OrderInfo>
                      </CardHeader>

                      <div className="flex items-center gap-2">
                        <ProductName>{ticket.name}</ProductName>
                        {giftBadgeLabel ? (
                          <span className="inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-600">
                            {giftBadgeLabel}
                          </span>
                        ) : null}
                      </div>

                      <ActionButtons>
                        {actions.showDetail && (
                          <DetailButton
                            onClick={() => {
                              setSelectOrder(ticket);
                              setTicketDetailModal(true);
                            }}
                          >
                              상세보기
                            </DetailButton>
                          )}
                      </ActionButtons>
                    </Card>
                  );
                })}

                {(query.hasNextPage || query.isFetchingNextPage) && (
                  <div
                    ref={loadMoreRef}
                    className="flex justify-center py-4 text-sm text-gray-500"
                    style={{ minHeight: 48 }}
                  >
                    {query.isFetchingNextPage
                      ? "티켓 내역을 더 불러오는 중..."
                      : "아래로 스크롤하면 더 불러옵니다."}
                  </div>
                )}
              </>
            )}
          </ContentArea>
        </div>
      </MobileViewContainer>

      <PurchaseTicketModal
        setVisible={setTicketModal}
        visible={ticketModal}
        info={selectOrder}
        setInfo={setSelectOrder}
        navigate={navigate}
        needRefresh={query.refetch}
      />
      <TicketPurchaseDetailModal
        setVisible={setTicketDetailModal}
        visible={ticketDetailModal}
        info={selectOrder}
        setInfo={setSelectOrder}
        onGift={
          getTicketPurchaseActions(selectOrder).showGift
            ? () => {
                setTicketDetailModal(false);
                setGiftModal(true);
              }
            : undefined
        }
        onRefund={
          getTicketPurchaseActions(selectOrder).showRefund
            ? () => {
                setTicketDetailModal(false);
                setTicketModal(true);
              }
            : undefined
        }
      />
      <TicketGiftModal
        setVisible={setGiftModal}
        visible={giftModal}
        info={selectOrder}
        setInfo={setSelectOrder}
        needRefresh={query.refetch}
      />
    </React.Fragment>
  );
}
